const APP_LOCATION =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://ringing.bnhs.org";

export const TABLE_VALUE_UNSPECIFIED = "Unspecified";

/** Table cells: null, undefined, or whitespace-only strings show as Unspecified. */
export const formatTableCell = (value) => {
  if (value === null || value === undefined) {
    return TABLE_VALUE_UNSPECIFIED;
  }
  if (typeof value === "string" && value.trim() === "") {
    return TABLE_VALUE_UNSPECIFIED;
  }
  return value;
};

const buildYearFileLink = (pathPrefix, year, fileName) => {
  if (!year || !fileName) {
    return "";
  }

  const normalizedAppLocation = APP_LOCATION.replace(/\/+$/, "");
  const normalizedPathPrefix = pathPrefix.replace(/^\/+|\/+$/g, "");
  return `${normalizedAppLocation}/${normalizedPathPrefix}/${encodeURIComponent(
    year
  )}/${encodeURIComponent(fileName)}`;
};

const buildImagePageLink = (year, fileName) =>
  buildYearFileLink("image_page", year, fileName);

const buildPagesLink = (year, fileName) =>
  buildYearFileLink("pages", year, fileName);

const parseCoordinateValue = (value, axis) => {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const upperRaw = raw.toUpperCase();
  if (upperRaw === "NULL" || upperRaw === "N/A" || upperRaw === "-") {
    return null;
  }

  const hemisphereMatch = upperRaw.match(/[NSEW]/);
  const hemisphere = hemisphereMatch ? hemisphereMatch[0] : null;
  const isNegativeHemisphere = hemisphere === "S" || hemisphere === "W";

  const normalized = upperRaw
    .replace(/[º°]/g, " ")
    .replace(/[′']/g, " ")
    .replace(/[″"]/g, " ")
    .replace(/,/g, " ");

  const numericParts = normalized.match(/-?\d+(?:\.\d+)?/g);
  if (!numericParts || numericParts.length === 0) {
    return null;
  }

  const numbers = numericParts.map((part) => Number.parseFloat(part));
  if (numbers.some((num) => Number.isNaN(num))) {
    return null;
  }

  const degrees = Math.abs(numbers[0]);
  const minutes = numbers[1] ? Math.abs(numbers[1]) : 0;
  const seconds = numbers[2] ? Math.abs(numbers[2]) : 0;
  let decimal = degrees + minutes / 60 + seconds / 3600;

  if (hemisphere) {
    if (axis === "lat" && !["N", "S"].includes(hemisphere)) {
      return null;
    }
    if (axis === "lon" && !["E", "W"].includes(hemisphere)) {
      return null;
    }
    if (isNegativeHemisphere) {
      decimal *= -1;
    }
  } else if (numbers[0] < 0) {
    decimal *= -1;
  }

  const limit = axis === "lat" ? 90 : 180;
  if (Math.abs(decimal) > limit) {
    return null;
  }

  return decimal;
};

const parseDecimalCoordinates = (latitude, longitude) => {
  const lat = parseCoordinateValue(latitude, "lat");
  const lon = parseCoordinateValue(longitude, "lon");

  if (lat === null || lon === null) {
    return null;
  }

  return { lat, lon };
};

export const formatDisplayDate = (value, options = {}) => {
  const {
    month = "long",
    day = "numeric",
    year = "numeric",
    includeTime = false,
  } = options;

  if (!value) {
    return TABLE_VALUE_UNSPECIFIED;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-US", {
    month,
    day,
    year,
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }
      : {}),
    timeZone: "UTC",
  });
};

/** qc_users.access: 1 = active, 0 = inactive. */
export const getQcAccessStatus = (access) => {
  if (access === null || access === undefined) {
    return null;
  }

  if (access === true || access === 1 || access === "1") {
    return { label: "Active", isActive: true };
  }

  if (access === false || access === 0 || access === "0") {
    return { label: "Inactive", isActive: false };
  }

  return null;
};

export const formatQcStatus = (status) => {
  const statusMap = {
    copied_to_avifauna: "Copied to Avifauna",
    duplicate: "Duplicate",
    blocked: "Blocked",
    claimed: "Claimed",
  };

  if (statusMap[status]) {
    return statusMap[status];
  }
  if (typeof status === "string" && status.includes("_")) {
    return status
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }
  return status || TABLE_VALUE_UNSPECIFIED;
};

const formatDecimalCoordinate = (value) => Number(value).toFixed(6);

const formatRemarksValue = (remarks, reportedBy, sourceLink) => {
  const remarksText = String(remarks ?? "").trim();
  const sanitizedReportedBy =
    String(reportedBy ?? "").trim() || TABLE_VALUE_UNSPECIFIED;
  const sanitizedSource =
    String(sourceLink ?? "").trim() || TABLE_VALUE_UNSPECIFIED;

  const lines = [];
  if (remarksText) {
    lines.push(remarksText);
  }
  lines.push(`Reported by: ${sanitizedReportedBy}`);
  lines.push(`Source: ${sanitizedSource}`);
  return lines.join("\n");
};

const hasSpeciesMismatch = (data) => {
  const ringingSpecies = data?.ringing_details?.ringing_species?.trim().toLowerCase();
  const resightingSpecies = data?.resighting_details?.scientific_name?.trim().toLowerCase();

  if (!ringingSpecies || !resightingSpecies) return false;
  return ringingSpecies !== resightingSpecies;
};

/** Digitization: Male | Female | Unknown | empty; Avifauna-style: M | F | Unknown. */
const CANONICAL_SEX = {
  m: "m",
  male: "m",
  f: "f",
  female: "f",
  unknown: "unknown",
};

export const normalizeComparableSex = (value) => {
  if (value === null || value === undefined) {
    return "unknown";
  }
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === "unspecified" || normalized === "n/a") {
    return "unknown";
  }
  return CANONICAL_SEX[normalized] ?? normalized;
};

const hasSexMismatch = (data) => {
  const ringingSex = normalizeComparableSex(data?.ringing_details?.sex);
  const resightingSex = normalizeComparableSex(data?.resighting_details?.sex);
  if (!ringingSex && !resightingSex) return false;
  return ringingSex !== resightingSex;
};

const formatScientificName = (scientificName, synonym) => {
  const name = formatTableCell(scientificName);
  return synonym ? `${name} (syn: ${synonym})` : name;
};

export const buildRecordDetailsFromApi = (data, options = {}) => {
  const {
    detailDateFormatOptions = {},
    usesPagesLinks = false,
  } = options;
  const resighting = data?.resighting_details || {};
  const ringing = data?.ringing_details || {};
  const buildFileLink = usesPagesLinks ? buildPagesLink : buildImagePageLink;
  const resightingPageLink = buildFileLink(
    resighting?.resighting_file_year,
    resighting?.resighting_file_name
  );

  const decimalCoordinates = parseDecimalCoordinates(
    resighting?.lattitude,
    resighting?.longitude
  );

  return {
    recordId: formatTableCell(resighting?.id),
    adminRemarks: String(data?.qc_assignment?.admin_remarks ?? "").trim(),
    mapCoordinates: decimalCoordinates,
    speciesMismatch: hasSpeciesMismatch(data),
    sexMismatch: hasSexMismatch(data),
    ringingComparisonMeta: {
      ringingSpecies: ringing?.ringing_species ?? "",
      speciesSynonym: ringing?.species_synonym ?? "",
    },
    ringingRows: [
      ["Ring number", formatTableCell(ringing?.ring_number)],
      ["Bird - Ringer", formatTableCell(ringing?.bird_ringer)],
      ["Ringing Species", formatScientificName(formatTableCell(ringing?.ringing_species), ringing?.species_synonym)],
      // ["Sex", formatTableCell(ringing?.sex)],
      ["Sex", ringing?.sex == null || ringing?.sex === "" ? "Unknown" : formatTableCell(ringing?.sex)],
      [
        "Bird - Ringing Date",
        formatDisplayDate(ringing?.bird_ringing_date, detailDateFormatOptions),
      ],
      ["Year of ringing", formatTableCell(ringing?.ringing_file_year)],
      [
        "Ringing Page Link",
        formatTableCell(
          buildFileLink(ringing?.ringing_file_year, ringing?.ringing_file_name)
        ),
      ],
      [
        "Date Of Ringing",
        formatDisplayDate(resighting?.date_of_ringing, detailDateFormatOptions),
      ],
      ["Place Of Ringing", formatTableCell(resighting?.place_of_ringing)],
      ["Ringer", formatTableCell(resighting?.ringer)],
    ],
    resightingRows: [
      // ["ID", formatTableCell(resighting?.id)],
      ["Ring number", formatTableCell(resighting?.ring_number)],
      ["Scientific Name", formatScientificName(formatTableCell(resighting?.scientific_name), resighting?.species_synonym)],
      // ["Sex", formatTableCell(resighting?.sex)],
      ["Sex", resighting?.sex == null || resighting?.sex === "" ? "Unknown" : formatTableCell(resighting?.sex)],
      ["Latitude", formatTableCell(resighting?.lattitude)],
      ["Longitude", formatTableCell(resighting?.longitude)],
      ["Place Of Recovery", formatTableCell(resighting?.place_of_recovery)],
      [
        "Coordinates (decimal)",
        decimalCoordinates
          ? `${formatDecimalCoordinate(decimalCoordinates.lat)}, ${formatDecimalCoordinate(
              decimalCoordinates.lon
            )}`
          : TABLE_VALUE_UNSPECIFIED,
      ],
      [
        "Date Of Recovery",
        formatDisplayDate(resighting?.date_of_recovery, detailDateFormatOptions),
      ],
      ["Reported By", formatTableCell(resighting?.reported_by)],
      [
        "Remarks",
        formatRemarksValue(
          resighting?.remarks,
          resighting?.reported_by,
          resightingPageLink
        ),
      ],
    ],
  };
};
